import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/AdminSidebar';
import { adminGetSurveyResults, adminGetSurvey } from '../../lib/adminApi';

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function RatingBar({ value, max }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 120, height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${(value / max) * 100}%`, height: '100%', background: '#0176CE', borderRadius: 4 }} />
      </div>
      <span style={{ fontSize: 13, fontWeight: 600, color: '#0a2540' }}>{value.toFixed(1)} / {max}</span>
    </div>
  );
}

export default function SurveyResults() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([adminGetSurveyResults(id), adminGetSurvey(id)])
      .then(([r, s]) => { setData(r); setSurvey(s.survey); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div style={s.page}><AdminSidebar active="surveys" /><main style={s.main}><div style={s.empty}>Loading…</div></main></div>;
  if (!data) return null;

  const { questions, sends, answers, stats } = data;

  // Build per-question aggregates
  const qMap = {};
  for (const q of questions) qMap[q.question_id] = { ...q, ans: [] };
  for (const a of answers) {
    if (qMap[a.question_id]) qMap[a.question_id].ans.push(a);
  }

  return (
    <div style={s.page}>
      <AdminSidebar active="surveys" />
      <main style={s.main}>
        <div style={s.header}>
          <div>
            <button onClick={() => navigate('/admin/surveys')} style={s.backBtn}>← Surveys</button>
            <h1 style={s.title}>{survey?.title}</h1>
          </div>
        </div>

        {/* Stats row */}
        <div style={s.statsRow}>
          {[
            { label: 'Sent', value: stats.total_sent },
            { label: 'Opened', value: stats.total_opened, pct: stats.total_sent ? Math.round(stats.total_opened / stats.total_sent * 100) : 0 },
            { label: 'Completed', value: stats.total_completed, pct: stats.total_sent ? Math.round(stats.total_completed / stats.total_sent * 100) : 0 },
          ].map(st => (
            <div key={st.label} style={s.statCard}>
              <div style={s.statVal}>{st.value}</div>
              <div style={s.statLabel}>{st.label}{st.pct != null ? ` (${st.pct}%)` : ''}</div>
            </div>
          ))}
        </div>

        {/* Per-question results */}
        <h2 style={s.sectionTitle}>Responses by Question</h2>
        {questions.map(q => {
          const qa = qMap[q.question_id];
          const numericAnswers = qa.ans.filter(a => a.answer_numeric != null);
          const avg = numericAnswers.length ? numericAnswers.reduce((s, a) => s + a.answer_numeric, 0) / numericAnswers.length : null;
          const max = q.question_type === 'rating_10' ? 10 : 5;

          return (
            <div key={q.question_id} style={s.qCard}>
              <div style={s.qHeader}>
                <span style={s.qText}>{q.question_text}</span>
                <span style={s.qType}>{q.question_type.replace('_', ' ')}</span>
              </div>
              {qa.ans.length === 0 && <div style={{ color: '#9ca3af', fontSize: 13 }}>No responses yet</div>}

              {/* Numeric rating summary */}
              {avg !== null && (
                <div style={{ marginBottom: 16 }}>
                  <RatingBar value={avg} max={max} />
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{numericAnswers.length} response{numericAnswers.length !== 1 ? 's' : ''}</div>
                </div>
              )}

              {/* Individual answers */}
              {qa.ans.length > 0 && (
                <table style={s.ansTable}>
                  <thead>
                    <tr>
                      <th style={s.ansTh}>Respondent</th>
                      <th style={s.ansTh}>Company</th>
                      <th style={s.ansTh}>Answer</th>
                      <th style={s.ansTh}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {qa.ans.map((a, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={s.ansTd}>{a.full_name}</td>
                        <td style={s.ansTd}>{a.company_name}</td>
                        <td style={s.ansTd}>
                          {a.answer_numeric != null
                            ? <strong>{a.answer_numeric}</strong>
                            : <span style={{ color: '#374151' }}>{a.answer_text || '—'}</span>
                          }
                        </td>
                        <td style={s.ansTd}>{fmtDate(a.completed_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}

        {/* Send log */}
        <h2 style={s.sectionTitle}>Send Log</h2>
        <div style={s.tableCard}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Recipient</th>
                <th style={s.th}>Company</th>
                <th style={s.th}>File</th>
                <th style={s.th}>Sent</th>
                <th style={s.th}>Opened</th>
                <th style={s.th}>Completed</th>
              </tr>
            </thead>
            <tbody>
              {sends.map(ss => (
                <tr key={ss.send_id} style={s.tr}>
                  <td style={s.td}>{ss.full_name}<br /><span style={{ fontSize: 11, color: '#9ca3af' }}>{ss.email}</span></td>
                  <td style={s.td}>{ss.company_name}</td>
                  <td style={s.td}><span style={{ fontSize: 12 }}>{ss.file_name || '—'}</span></td>
                  <td style={s.td}>{fmtDate(ss.sent_at)}</td>
                  <td style={s.td}>{ss.opened_at ? <span style={{ color: '#059669' }}>✓ {fmtDate(ss.opened_at)}</span> : <span style={{ color: '#9ca3af' }}>—</span>}</td>
                  <td style={s.td}>{ss.completed_at ? <span style={{ color: '#059669' }}>✓ {fmtDate(ss.completed_at)}</span> : <span style={{ color: '#9ca3af' }}>—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

const s = {
  page:        { display: 'flex', minHeight: '100vh', background: '#f8fafc' },
  main:        { flex: 1, padding: '36px 40px', overflowY: 'auto' },
  header:      { marginBottom: 24 },
  backBtn:     { background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 13, padding: '0 0 8px', display: 'block' },
  title:       { fontSize: 24, fontWeight: 700, color: '#0a2540', margin: 0 },
  statsRow:    { display: 'flex', gap: 16, marginBottom: 32 },
  statCard:    { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '20px 24px', flex: 1 },
  statVal:     { fontSize: 32, fontWeight: 700, color: '#0a2540' },
  statLabel:   { fontSize: 13, color: '#6b7280', marginTop: 4 },
  sectionTitle:{ fontSize: 16, fontWeight: 700, color: '#0a2540', margin: '0 0 16px' },
  qCard:       { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '20px 24px', marginBottom: 16 },
  qHeader:     { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 },
  qText:       { fontSize: 15, fontWeight: 600, color: '#0a2540', flex: 1 },
  qType:       { fontSize: 11, color: '#6b7280', background: '#f1f5f9', padding: '3px 8px', borderRadius: 4, whiteSpace: 'nowrap', marginLeft: 12 },
  ansTable:    { width: '100%', borderCollapse: 'collapse' },
  ansTh:       { padding: '8px 12px', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left' },
  ansTd:       { padding: '10px 12px', fontSize: 13, color: '#374151', verticalAlign: 'top' },
  tableCard:   { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', marginBottom: 32 },
  table:       { width: '100%', borderCollapse: 'collapse' },
  th:          { padding: '10px 16px', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', whiteSpace: 'nowrap' },
  tr:          { borderBottom: '1px solid #f1f5f9' },
  td:          { padding: '12px 16px', fontSize: 13, color: '#374151', verticalAlign: 'middle' },
  empty:       { color: '#9ca3af', padding: '40px', textAlign: 'center' },
};
